import React from "react";
import { TemplateDownloadUrl } from "./Constants";
import { QQLink } from "common/components/QQLink";
import { toggleGenAiFileParsing } from "common/services";

export enum ErrorType {
  Generic = 1,
  ShipTo,
  ParseNoHeader,
  ParseLength,
  ParseDbSave,
  ParseFileStorage,
  ParseNoItems,
  QuoteDownload,
  QuoteSave,
  NoUIDFound,
  ParseNoShape,
  ParseInvalidSize,
  ParseNoGrade,
  ParseNoQuantity,
  SequenceMissing,
  UploadLimitReached,
  SizeInvalidValues,
  UploadFormatInvalid,
  UploadMultiple,
  FileTooLarge,
  UploadDetailMessage,
  NetworkError,
}

export interface ErrorMessage {
  title: string;
  message: React.ReactNode;
}

const invalidFormatTypeMessage = () =>
  toggleGenAiFileParsing
    ? "The file format you have selected is not currently supported. Please select an .XLSX file to continue."
    : "The file format you have selected is not currently supported. Please select an .XLSX or PDF file to continue.";

export const ErrorMap = new Map<number, ErrorMessage>([
  [
    ErrorType.Generic,
    {
      title: "Service Error",
      message: "We encountered an internal error. Our service may be down, Please try again later.",
    },
  ],
  [ErrorType.ShipTo, { title: "Ship-To Error", message: "City, State not found." }],
  [
    ErrorType.ParseNoHeader,
    {
      title: "Uploaded File Error",
      message: (
        <>
          The file you have uploaded does not have a header row. Please try again or{" "}
          <QQLink href={TemplateDownloadUrl} download="export">
            download
          </QQLink>{" "}
          the template file.
        </>
      ),
    },
  ],
  [
    ErrorType.ParseLength,
    {
      title: "Uploaded File Error",
      message:
        "The file you have uploaded has lengths we do not recognize. Please review and try again.",
    },
  ],
  [
    ErrorType.ParseDbSave,
    {
      title: "Service Error",
      message: "Unable save items at this time. Our service may be down, Please try again later.",
    },
  ],
  [
    ErrorType.ParseFileStorage,
    {
      title: "Service Error",
      message:
        "Unable to retrieve items uploaded file from storage. Our service may be down, Please try again later.",
    },
  ],
  [
    ErrorType.ParseNoItems,
    {
      title: "Upload Error",
      message:
        "Unable to retrieve items from your uploaded file. Verify the file you have uploaded is not Empty.",
    },
  ],
  [
    ErrorType.QuoteDownload,
    {
      title: "Service Error",
      message:
        "Unable to download your quote at this time. Our service may be down, Please try again later.",
    },
  ],
  [
    ErrorType.QuoteSave,
    {
      title: "Service Error",
      message:
        "Unable to save your quote at this time. Our service may be down, Please try again later.",
    },
  ],
  [ErrorType.NoUIDFound, { title: "Uploaded File Error", message: "File was not found." }],
  [
    ErrorType.ParseNoShape,
    {
      title: "Uploaded File Error",
      message: <>The file is missing a Shape entry. Please review and try again.</>,
    },
  ],
  [
    ErrorType.ParseInvalidSize,
    {
      title: "Upload Failed",
      message: <>The file is missing a Size entry. Please review and try again.</>,
    },
  ],
  [
    ErrorType.ParseNoGrade,
    {
      title: "Upload Failed",
      message: <>The file is missing a Grade entry. Please review and try again.</>,
    },
  ],
  [
    ErrorType.ParseNoQuantity,
    {
      title: "Upload Failed",
      message: <>The file is missing a Quantity entry. Please review and try again.</>,
    },
  ],
  [
    ErrorType.SequenceMissing,
    {
      title: "Upload Failed",
      message: <>The file is missing a Sequence entry. Please review and try again.</>,
    },
  ],
  [
    ErrorType.UploadLimitReached,
    {
      title: "Upload Error",
      message:
        "The uploaded file contains more than 100 rows. Please split the file into smaller parts, each containing up to 100 rows, and upload them separately",
    },
  ],
  [
    ErrorType.SizeInvalidValues,
    {
      title: "Upload Error",
      message:
        "The file you have uploaded has size values we do not recognize. Please review and try again.",
    },
  ],
  [
    ErrorType.UploadFormatInvalid,
    {
      title: "Upload Failed",
      message: invalidFormatTypeMessage(),
    },
  ],
  [
    ErrorType.UploadMultiple,
    { title: "Upload Error", message: "Please select a single file to upload." },
  ],
  [
    ErrorType.FileTooLarge,
    {
      title: "Unfortunately, This file's too large.",
      message: (
        <>
          <b>Option 1</b>: Enter the quotation request manually using the Excel template.
          <br />
          <b>Option 2</b>: If you are quoting a Material Buy List, consider splitting the quotation
          into multiple PDFs.
        </>
      ),
    },
  ],
  [
    ErrorType.UploadDetailMessage,
    {
      title: "File Validation Error",
      message: "There was an issue with the data in your file. Please review and try again.",
    },
  ],
  [
    ErrorType.NetworkError,
    {
      title: "Connection Problem",
      message:
        "We couldn't reach the server. Please check your connection and try again. If the problem persists, the service may be temporarily unavailable.",
    },
  ],
]);

/**
 * Heuristic for detecting browser-side fetch failures (server unreachable,
 * DNS failure, offline). Mirrors GlobalErrorHandlers.isNetworkError.
 */
export const isNetworkFailure = (err: unknown): boolean => {
  if (err instanceof TypeError) {
    return /failed to fetch|networkerror|load failed|network request failed/i.test(err.message);
  }
  // RTK Query / fetch wrappers often wrap the error
  const anyErr = err as { error?: unknown; message?: string; status?: string | number };
  if (anyErr?.status === "FETCH_ERROR") return true;
  if (anyErr?.error instanceof TypeError && /failed to fetch/i.test(anyErr.error.message)) {
    return true;
  }
  if (
    typeof anyErr?.message === "string" &&
    /failed to fetch|networkerror|load failed/i.test(anyErr.message)
  ) {
    return true;
  }
  return false;
};

export const BackendErrorCodeMap = new Map<string, ErrorType>([
  ["Error.Too.Large", ErrorType.FileTooLarge],
  ["File.Upload.DetailError", ErrorType.UploadDetailMessage],
]);

const errorManager =
  (log: typeof console.log) =>
  (error: Map<number, ErrorMessage>) =>
  (type: unknown, message?: React.ReactNode) => {
    let returnMessage: ErrorMessage = { title: "", message: null };
    // `type` is normally a string error code, but callers can pass a thrown Error object
    // (e.g. when a 403/empty body fails JSON parsing). Coerce to a string so we never crash
    // with "type.includes is not a function" and instead fall back to a generic message.
    const typeKey = typeof type === "string" ? type : String((type as any)?.message ?? type ?? "");
    let errorMessage = error.get(Number(typeKey));
    if (errorMessage) {
      returnMessage.title = errorMessage.title;
      returnMessage.message = message ?? errorMessage.message;
    } else {
      log(`Error id: ${typeKey} not found, using generic message`);
      errorMessage = typeKey.includes("xlsx")
        ? error.get(ErrorType.UploadFormatInvalid)
        : error.get(ErrorType.Generic);
      returnMessage = errorMessage ?? returnMessage;
    }
    return returnMessage;
  };

export const getErrorMessageByCode = (errorCode: string): ErrorMessage => {
  const errorType = BackendErrorCodeMap.get(errorCode);
  if (errorType) {
    const errorMessage = ErrorMap.get(errorType);
    if (errorMessage) {
      return errorMessage;
    }
  }
  const genericError = ErrorMap.get(ErrorType.Generic);
  return genericError || { title: "Error", message: "An unknown error occurred" };
};

export const getErrorMessage = errorManager(console.log)(ErrorMap);
